/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useEffect, useRef, useState } from "react";
import { ArrowUpTrayIcon } from "@heroicons/react/24/solid";
import { Formik, Form, ErrorMessage } from "formik";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// @types
import { CreateServiceRequest, RequestFormValues, UploadImagesRequest } from "../../../types/request.types";
import { UserRole } from "../../../types/auth.types";
import { CategoryResponse } from "../../../types/category.types";

// @schemas
import { getDynamicFormSchema } from "../../../schemas/dynamicFormSchema";

// @components
import { FormField } from "../../../components/atoms/FormField";
import Modal from "../../../components/Modals/ConfirmationModal";
import Button from "../../../components/atoms/Button";
import LoginModal from "../../../components/Modals/LoginModal";

// @store
import useServiceStore from "../../../store/useServiceStore";
import useServiceRequestStore from '../../../store/useServiceRequestStore';
import { useModalStore } from "../../../store/useModalStore";
import useAuthStore from "../../../store/useAuthStore";
import { useCompanyStore } from "../../../store/useCompanyStore";
import { useSpinnerStore } from "../../../store/useSpinnerStore";
import useCategoryStore from "../../../store/useCategoryStore";

// @services
import locationServices from "../../../services/location.service"

const defaultValues: RequestFormValues = {
  patientName: "",
  phone: "",
  city: 0,
  address: "",
  idealDate: "",
  idealTime: "",
  services: [],
  medicalOrder: null,
  description: "",
  requireHomeService: false,
  requireVirtualService: false,
  requireUrgentService: false,
};

export const DynamicForm: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const img = location.state.urlImg;
  const { isAuthenticated, roleList } = useAuthStore();
  const { data, userData } = useCompanyStore();
  const { openModal } = useModalStore();
  const { getServicesBySubCategory, servicesWithAccess, } = useServiceStore();
  const { openSpinner } = useSpinnerStore();
  const {
    addServiceRequestWithImages,
    resetStore,
    isLoading,
  } = useServiceRequestStore();
  const { fetchCategory } = useCategoryStore();
  const [initialValues, setInitialValues] = useState<RequestFormValues>(defaultValues);
  const { categoryId, subCategoryId } = useParams<{ categoryId: string, subCategoryId: string }>();
  const [guestFormData, setGuestFormData] = useState<RequestFormValues | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const formikRef = useRef<any>(null);
  const [cities, setCities] = useState<{ value: number, label: string, disabled?: boolean, }[]>([]);
  const [category, setCategory] = useState<CategoryResponse>();


  const getCategory = async () => {
    openSpinner(true);
    try {
      const response = await fetchCategory(categoryId ?? '');
      setCategory(response);
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.callList"));
    }
  }

  useEffect(() => {
    if (categoryId && subCategoryId) {
      getServicesBySubCategory(categoryId, subCategoryId);
      getCategory();
    }
    if (isAuthenticated) {
      setInitialValues((prevValues) => ({
        ...prevValues,
        patientName: getUserByType(),
      }));
    }
  }, [])

  const getCities = async () => {
    // for now, the id is by default antioquia
    const departmentId = 2;
    openSpinner(true);
    try {
      const response = await locationServices.getCityByDepartment(departmentId);
      const newList = response?.data?.map((city) => {
        return { value: city.id, label: city.name, disabled: false, }
      })
      setCities([{ value: 0, label: t("categoryService.selectCity"), disabled: true, }, ...newList]);
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.callList"));
    }
  }

  useEffect(() => {
    getCities();
  }, [])

  const getUserByType = () => {
    if (roleList?.includes(UserRole.PROFESSIONAL)) return data?.name ?? "";
    return `${userData?.names ?? ''} ${userData?.lastNames ?? ''}`;
  }

  const validateModeService = (values: RequestFormValues): number => {
    if (values.requireHomeService && values.requireVirtualService) {
      toast.error(t("categoriesPage.textValidation"));
    }
    if (values.requireHomeService) {
      return 1;
    } else if (values.requireVirtualService) {
      return 2;
    } else {
      return 0;
    }
  }

  const confirmRequest = (): void => {
    resetStore();
    return navigate('/');
  }

  const handleConfirmInformation = () => {
    return openModal('information', {
      title: t("categoriesPage.completedInformation"),
      message: t("categoriesPage.succesMessage"),
      confirmButtonText: t("categoriesPage.acceptButton"),
      confirmButtonColor: 'bg-dark-bgButtonSuccess',
      onConfirm: () => { return confirmRequest() },
    });
  };

  // Add this effect to handle post-login form submission
  useEffect(() => {
    if (isAuthenticated && guestFormData) {
      handleSubmit(guestFormData);
      setGuestFormData(null);
    }
  }, [isAuthenticated]);

  // Add this function to handle guest form submission
  const handleGuestSubmit = (values: RequestFormValues) => {
    setGuestFormData(values);
    setShowLoginModal(true);
  };

  const handleSubmit = async (values: RequestFormValues) => {
    validateModeService(values);
    const newObject: CreateServiceRequest = {
      patientName: values.patientName,
      priority: values.requireUrgentService ? 1 : 0,
      isDirect: false,
      serviceNeededBy: values.idealTime ? new Date(`${values.idealDate}T${values.idealTime}Z`) : new Date(values.idealDate).toISOString(),
      distanceFromCompany: 0,
      description: values.description || "",
      phoneNumber: values.phone,
      cityId: 12,
      serviceModeId: validateModeService(values),
      address: values.address,
      services: values.services.map((serviceId) => ({ serviceId })),
      subCategoryId: subCategoryId ?? "",
      requestedService: ""
    };
    const imagesData: UploadImagesRequest = { files: [values.medicalOrder as File] };
    sendData(newObject, imagesData);
    console.log(newObject)
  };



  const sendData = async (newObject: CreateServiceRequest, imagesData: UploadImagesRequest) => {
    openSpinner(true);
    try {
      await addServiceRequestWithImages(newObject, imagesData);
      handleConfirmInformation();
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      toast.error(t("categoriesPage.hasError"));
    }
  }

  const dynamicFormSchema = getDynamicFormSchema(t, category?.name ?? '');

  const needsOrder = (exams: string[], state?: string): boolean => {
    if (!state) {
      return exams.some((examId) =>
        servicesWithAccess.some((service) => service.serviceId === examId && service.requiredFile)
      );
    }

    return exams.some((examId) => {
      const service = servicesWithAccess?.find((service) => service.serviceId === examId);
      if (!service) return false;

      switch (state) {
        case 'canBeDoneAtHome':
          return service.canBeDoneAtHome;
        case 'canBeDoneVirtually':
          return service.canBeDoneVirtually;
        default:
          return false;
      }
    });
  };

  const handleChangeSelectServices = (e: any) => {
    const value = e.target.value;
    if (formikRef.current.values.services.includes(value)) {
      e.target.value = "";
      return;
    } else {
      if (e.target.value) {
        const newExams = [
          ...formikRef.current.values.services,
          e.target.value,
        ];
        formikRef.current.setFieldValue("services", newExams);
        e.target.value = "";
      }
      e.target.value = "";
      return;
    }
  }

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={dynamicFormSchema}
        onSubmit={isAuthenticated ? handleSubmit : handleGuestSubmit}
        validateOnChange={true}
        validateOnBlur={true}
        enableReinitialize={true}
      >
        {({ setFieldValue, values }) => {

          return (
            <Form className="w-full mx-auto p-4 lg:py-6 bg-white rounded-lg">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6  ">
                <div className="space-y-6 ">
                  {/* Patient Name */}
                  <FormField
                    label={t("categoriesPage.form.name")}
                    name="patientName"
                    type="text"
                    placeholder={t("categoriesPage.form.placeholderName")}
                    className="border border-gray-300 rounded-md"
                  />
                  {/* Phone */}
                  <FormField
                    label={t("categoriesPage.form.phone")}
                    name="phone"
                    placeholder={t("categoriesPage.form.placeholderPhone")}
                    type="text"
                    className="border border-gray-300 rounded-md"
                  />

                  {/* City */}
                  <FormField
                    label={t("categoriesPage.form.city")}
                    name="city"
                    as="select"
                    options={cities}
                    className="border border-gray-300 rounded-md"
                  />

                  <FormField
                    label={t("categoriesPage.form.address")}
                    name="address"
                    placeholder={t("categoriesPage.form.placeholderAddress")}
                    className="border border-gray-300 rounded-md"
                  />

                  {/* Date and Time */}
                  <FormField
                    type="date"
                    label={t("categoriesPage.form.date")}
                    name="idealDate"
                    className="border border-gray-300 rounded-md"
                  />
                  {category?.name !== "Carpinteria"
                    ? <FormField
                      label={t("categoriesPage.form.hour")}
                      name="idealTime"
                      type="time"
                      className="border border-gray-300 rounded-md"
                    />
                    : null
                  }
                  {/* Exam Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 text-left lg:mb-0 mb-4">
                      {t("categoriesPage.form.selectService")}
                    </label>
                    <div className="mt-2 space-y-2">
                      {values.services?.map((exam) => (
                        <div
                          key={exam}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-300"
                        >
                          <span className="text-[14px] text-gray-400">
                            {servicesWithAccess.find((s: any) => s.serviceId === exam)?.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              const newExams = values.services.filter(
                                (e) => e !== exam
                              );
                              setFieldValue("services", newExams);
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                      <select
                        name="services"
                        onChange={(e) => handleChangeSelectServices(e)}
                        className=" text-[14px] truncate mt-1 block w-full rounded-md border border-gray-500shadow-sm focus:border-blue-500 focus:ring-blue-500 placeholder-cTextSecondary px-3 py-2"
                      >
                        <option className="text-[12px]" value="">{t("categoriesPage.form.chooseTex")}</option>
                        {servicesWithAccess
                          .map((service) => (
                            <option className=" w-full truncate text-[12px]" key={service.serviceId} value={service.serviceId}>
                              {service.name}
                            </option>
                          ))}
                      </select>
                      <ErrorMessage
                        name="services"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-6 ">
                  {/* Medical Order Upload */}
                  {needsOrder(values.services, undefined) && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 text-left">
                        {t("categoriesPage.form.medicalOrder")}
                      </label>
                      <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                        <div className="space-y-1 text-center">
                          {values.medicalOrder ? (
                            <div className="flex flex-col items-center">
                              <div className="flex items-center space-x-2">
                                <ArrowUpTrayIcon className="size-8 text-green-500" />
                                <span className="text-sm text-gray-600">
                                  {values.medicalOrder.name}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() =>
                                  setFieldValue("medicalOrder", null)
                                }
                                className="mt-2 text-sm text-red-500 hover:text-red-700"
                              >
                                {t("categoriesPage.form.deleteFile")}
                              </button>
                            </div>
                          ) : (
                            <>
                              <ArrowUpTrayIcon className="mx-auto size-12 text-gray-400" />
                              <div className="flex text-sm text-gray-600">
                                <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                  <span>{t("categoriesPage.form.uploadFile")}</span>
                                  <input
                                    type="file"
                                    className="sr-only"
                                    onChange={(event) => {
                                      const file =
                                        event.currentTarget.files?.[0] || null;
                                      if (file) {
                                        // Verificar el tamaño del archivo (2MB = 2 * 1024 * 1024 bytes)
                                        if (file.size > 2 * 1024 * 1024) return toast.error(t("categoriesPage.form.errorFile"));
                                        // Verificar el tipo de archivo
                                        const allowedTypes = [
                                          "image/jpeg",
                                          "image/png",
                                          "application/pdf",
                                        ];
                                        if (!allowedTypes.includes(file.type)) return toast.error(t("categoriesPage.form.allowedTypes"));
                                      }
                                      setFieldValue("medicalOrder", file);
                                    }}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                  />
                                </label>
                              </div>
                              <p className="text-xs text-gray-500">
                                {t("categoriesPage.form.heavyDocument")}
                              </p>
                            </>
                          )}
                        </div>
                      </div>
                      <ErrorMessage
                        name="medicalOrder"
                        component="div"
                        className="text-red-500 text-sm mt-1"
                      />
                    </div>
                  )}
                  {/* Description */}
                  <FormField
                    label={t("categoriesPage.form.description")}
                    name="description"
                    as="textarea"
                    rows={12}
                    className="text-[14px] leading-[24px] text-gray-500 font-normal border border-gray-300 rounded-md "

                  />
                  {/* Service Options */}
                  {needsOrder(values.services, 'canBeDoneAtHome') &&
                    (
                      <FormField
                        label={t("categoriesPage.form.homeExam")}
                        name="requireHomeService"
                        as="switch"
                        value={values.requireHomeService}
                        onChange={(value) => { setFieldValue("requireHomeService", value) }}
                      />
                    )
                  }
                  {needsOrder(values.services, 'canBeDoneVirtually') &&
                    (
                      <FormField
                        label={t("categoriesPage.form.virtualExam")}
                        name="requireVirtualService"
                        as="switch"
                        value={values.requireVirtualService}
                        onChange={(value) => setFieldValue("requireVirtualService", value)}
                      />
                    )
                  }

                </div>
                <div className=" flex flex-col items-center">
                  <label className="text-sm self-start font-medium text-gray-700 mb-1">{t("categoriesPage.form.subCategory")}</label>
                  {/* Image Placeholder */}
                  <div className="w-full h-[200px] lg:h-[300px] bg-gray-200 rounded-md mb-8">

                    <img src={img} alt="img" className="w-full h-[200px] lg:h-full overflow-hidden object-cover rounded-xl "></img>

                  </div>
                  {/* Urgent Service */}
                  <div className="w-full flex justify-between items-center">
                    <FormField
                      label={t("categoriesPage.form.urgenService")}
                      name="requireUrgentService"
                      as="switch"
                      value={values.requireUrgentService}
                      onChange={(value) => setFieldValue("requireUrgentService", value)}
                    />
                    {/* Submit Button */}
                    <Button
                      type="submit"
                      variant="filled"
                      size="medium"
                      disabled={isLoading}
                      className="inline-flex justify-center py-2 px-4"
                    >
                      {t("categoriesPage.form.request")}
                    </Button>
                  </div>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>


      {showLoginModal && (
        <LoginModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
        />
      )}
      <Modal />
    </>
  );
};


export default DynamicForm;
